// Replay & validation (the quality engine, PRD §12). Replay the trace through
// the model as a design reviewer: for each decision, judge whether the chosen
// option holds up under fuller reasoning. Verdicts annotate the graph and
// feed the doc synthesiser's Open Questions.

import { complete } from "../api/model";
import { PRI } from "../api/scheduler";
import { parseModelJSON } from "../lib/json-repair";
import { tidyProse } from "../lib/text";
import { save } from "./persistence";
import { judgePrompt } from "./prompts";
import { traceParseOutcome } from "./telemetry";
import { groundingLine, pathTo, state, type DecisionNode, type Verdict } from "./state";

export function resolvedDecisions(): DecisionNode[] {
  // depth-0 is "which area first" (not a soundness-checkable design call)
  return [...state.nodes.values()].filter((n) => n.depth > 0 && n.chosenIndex != null)
    .sort((a, b) => a.depth - b.depth);
}

export async function judgeDecision(node: DecisionNode): Promise<Verdict> {
  const chosen = node.options[node.chosenIndex!]!;
  const priorPath = pathTo(node).slice(0, -1)
    .filter((a) => a.chosenIndex != null && a.depth > 0)
    .map((a) => `- ${a.question} → ${a.options[a.chosenIndex!]?.label}`).join("\n") || "(none)";
  const prompt = judgePrompt(
    { idea: state.idea, grounding: groundingLine("n/a"), facts: state.facts, constraints: state.constraints },
    node, chosen, priorPath,
  );
  const raw = await complete(prompt, { max: 200, temp: 0.4, priority: PRI.FG, kind: "judge" });
  const { data, parse } = parseModelJSON(raw);
  traceParseOutcome("judge", parse, raw);
  if (!data) throw new Error("judge json unusable");
  const verdict = ["sound", "weak", "risky"].includes(data.verdict) ? data.verdict : "sound";
  return { verdict, note: tidyProse(data.note || ""), better: tidyProse(data.better || "") };
}

/** Replay every resolved decision; onEach(node) fires as each verdict lands. */
export async function validateTrace(onEach?: (node: DecisionNode) => void): Promise<DecisionNode[]> {
  const decisions = resolvedDecisions();
  for (const node of decisions) {
    try { node.verdict = await judgeDecision(node); }
    catch { node.verdict = { verdict: "sound", note: "(review unavailable)", better: "" }; }
    if (onEach) onEach(node);
  }
  save();
  return decisions;
}

/** Flagged replay findings, formatted for the synthesis prompt. */
export function reviewFindings(): string {
  const flagged = resolvedDecisions().filter((d) => d.verdict && d.verdict.verdict !== "sound");
  if (!flagged.length) return "";
  return flagged.map((d) =>
    `- [${d.verdict!.verdict}] ${d.question} (chose "${d.options[d.chosenIndex!]?.label}"): ${d.verdict!.note}${d.verdict!.better ? ` — stronger option: ${d.verdict!.better}` : ""}`
  ).join("\n");
}
